(() => {
  if (window.PileniumCart) return;

  const config = window.PileniumCartConfig || {};
  const labels = config.labels || {};
  const pending = new WeakMap();
  let mutations = Promise.resolve();
  let countRevision = 0;

  function endpoint(name) {
    const root = window.Shopify?.routes?.root || config.root || '/';
    const configured = config.routes?.[name];
    if (configured) return configured;
    return `${root.replace(/\/?$/, '/')}${name === 'cart' ? 'cart.js' : 'cart/add.js'}`;
  }

  function cartPage() {
    return /\/cart\/?$/.test(window.location.pathname);
  }

  function updateCount(cart) {
    if (!Number.isFinite(Number(cart?.item_count))) return;
    countRevision += 1;
    const count = Number(cart.item_count);
    document.querySelectorAll('[data-pil-cart-count]').forEach((node) => {
      node.textContent = String(count);
      node.hidden = count === 0;
    });
    document.querySelectorAll('#cart-icon-bubble .cart-count-bubble').forEach((node) => {
      const value = node.querySelector('[aria-hidden="true"]');
      if (value) value.textContent = String(count);
      node.hidden = count === 0;
    });
    return cart;
  }

  async function readCart() {
    const response = await fetch(endpoint('cart'), {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error(labels.error);
    const cart = await response.json();
    if (!Array.isArray(cart.items) || !Number.isFinite(Number(cart.item_count))) throw new Error(labels.error);
    return cart;
  }

  async function refreshCount() {
    const revision = countRevision;
    const cart = await readCart();
    if (revision === countRevision) updateCount(cart);
    return cart;
  }

  function renderDrawer(html, cart, options) {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return false;
    const fresh = new DOMParser().parseFromString(html || '', 'text/html').querySelector('#CartDrawer');
    const current = drawer.querySelector('#CartDrawer');
    if (!fresh || !current) return false;
    current.innerHTML = fresh.innerHTML;
    drawer.classList.toggle('is-empty', cart.item_count === 0);
    drawer.querySelector('.drawer__inner')?.classList.toggle('is-empty', cart.item_count === 0);
    const overlay = drawer.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', () => drawer.close?.());
    if (options.open) {
      if (typeof drawer.open === 'function') drawer.open(options.trigger);
      else {
        drawer.classList.add('animate', 'active');
        document.body.classList.add('overflow-hidden');
      }
    }
    return true;
  }

  async function refresh(options = {}) {
    const cart = options.cart || await readCart();
    updateCount(cart);
    let sections = options.sections;
    if (document.querySelector('cart-drawer') && !sections?.['cart-drawer']) {
      const url = new URL(window.location.pathname, window.location.origin);
      url.searchParams.set('sections', 'cart-drawer');
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(labels.error);
      sections = await response.json();
    }
    if (document.querySelector('cart-drawer') && !renderDrawer(sections?.['cart-drawer'], cart, options)) throw new Error(labels.error);
    return cart;
  }

  function statusNode(form, trigger) {
    const host = trigger?.closest('.pil-mcta__inner,[data-pdp-sticky],.pil-cu__card,.pil-upsell-card,.pfav__card') || form || trigger?.parentElement || document.body;
    let node = Array.from(host.children).find((child) => child.hasAttribute('data-pilenium-cart-status'));
    if (!node) {
      node = document.createElement('p');
      node.setAttribute('data-pilenium-cart-status', '');
      node.className = 'pilenium-cart-status';
      node.setAttribute('role', 'status');
      node.setAttribute('aria-live', 'polite');
      host.appendChild(node);
    }
    return node;
  }

  function announce(message, form, trigger, error = false) {
    const node = statusNode(form, trigger);
    node.textContent = message || '';
    node.classList.toggle('pilenium-cart-status--error', error);
    node.setAttribute('role', error ? 'alert' : 'status');
  }

  function lock(form, trigger) {
    const controls = new Set(form?.querySelectorAll('button[type="submit"],input[type="submit"]') || []);
    if (trigger) controls.add(trigger);
    if (form?.matches('[data-pdp-form],[data-acc-form]')) {
      document.querySelectorAll('[data-pdp-atc],[data-pdp-buynow],[data-pdp-sticky-buzz],[data-mcta-add],[data-mcta-slide],[data-pdp-slide],[data-pdp-sticky-puck],.pil-orbm--add,.pil-orbm--buy').forEach((control) => controls.add(control));
    }
    const states = Array.from(controls, (control) => ({ control, disabled: control.disabled, busy: control.getAttribute('aria-busy'), ariaDisabled: control.getAttribute('aria-disabled') }));
    states.forEach(({ control }) => {
      if ('disabled' in control) control.disabled = true;
      control.setAttribute('aria-busy', 'true');
      control.setAttribute('aria-disabled', 'true');
    });
    trigger?.classList.add('is-loading');
    form?.setAttribute('aria-busy', 'true');
    return () => {
      states.forEach(({ control, disabled, busy, ariaDisabled }) => {
        if ('disabled' in control) control.disabled = disabled;
        if (busy === null) control.removeAttribute('aria-busy');
        else control.setAttribute('aria-busy', busy);
        if (ariaDisabled === null) control.removeAttribute('aria-disabled');
        else control.setAttribute('aria-disabled', ariaDisabled);
      });
      trigger?.classList.remove('is-loading');
      form?.removeAttribute('aria-busy');
    };
  }

  function showAdded(trigger) {
    if (!trigger) return;
    const label = trigger.querySelector('.pil-acc__add-t,.pil-cu__add-t,.pil-upsell-add__txt,.pil-orbm__lbl') || (trigger.matches('[data-pdp-atc]') ? trigger.lastElementChild : trigger.matches('.pfav__atc') ? trigger : null);
    const original = label?.innerHTML;
    if (label) label.textContent = labels.added;
    const classes = ['is-added', 'is-ok', 'pil-added'];
    if (trigger.matches('[data-pil-add]')) classes.push('is-done');
    if (trigger.matches('[data-cart-add]')) classes.push('pil-cu__add--ok');
    trigger.classList.add(...classes);
    window.setTimeout(() => {
      trigger.classList.remove(...classes);
      if (label && original !== undefined) label.innerHTML = original;
    }, 1600);
  }

  function publishCart(cart, variantId) {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'pilenium-cart', productVariantId: variantId, cartData: cart });
    }
    document.dispatchEvent(new CustomEvent('pilenium:cart-updated', { detail: { cart } }));
  }

  function add(payload, options = {}) {
    const form = options.form || null;
    const trigger = options.trigger || form?.querySelector('[type="submit"]');
    const group = form || trigger;
    if (group && pending.has(group)) return pending.get(group);
    countRevision += 1;
    const release = lock(form, trigger);
    announce(labels.loading, form, trigger);
    let body;
    let headers = { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' };
    let variantId;
    if (payload instanceof FormData) {
      body = new FormData();
      payload.forEach((value, key) => body.append(key, value));
      variantId = body.get('id');
      body.set('sections', 'cart-drawer');
      body.set('sections_url', window.location.pathname);
    } else {
      variantId = payload.id || payload.items?.[0]?.id;
      body = JSON.stringify({ ...payload, sections: 'cart-drawer', sections_url: window.location.pathname });
      headers['Content-Type'] = 'application/json';
    }
    const operation = mutations.catch(() => {}).then(async () => {
      let response;
      try { response = await fetch(endpoint('add'), { method: 'POST', credentials: 'same-origin', headers, body }); }
      catch { throw new Error(labels.error); }
      let result;
      try { result = await response.json(); } catch { throw new Error(labels.error); }
      if (!response.ok || result.status || result.errors || !(result.id || result.items?.length)) {
        const message = typeof result.description === 'string' ? result.description : typeof result.message === 'string' ? result.message : labels.error;
        throw new Error(message);
      }
      announce(labels.added, form, trigger);
      if (options.checkout) {
        window.location.assign(config.routes?.checkout || `${(window.Shopify?.routes?.root || config.root || '/').replace(/\/?$/, '/')}checkout`);
        return result;
      }
      showAdded(trigger);
      if (cartPage()) {
        window.location.reload();
        return result;
      }
      try {
        const modal = form?.closest('quick-add-modal');
        modal?.hide?.(true);
        const cart = await refresh({ sections: result.sections, open: true, trigger });
        publishCart(cart, variantId);
        if (!document.querySelector('cart-drawer')) window.location.assign(config.routes?.cartPage || '/cart');
      } catch {
        window.location.assign(config.routes?.cartPage || '/cart');
      }
      return result;
    }).catch((error) => {
      announce(error.message || labels.error, form, trigger, true);
      throw error;
    }).finally(() => {
      release();
      if (group) pending.delete(group);
    });
    if (group) pending.set(group, operation);
    mutations = operation.catch(() => {});
    return operation;
  }

  function addForm(form, options = {}) {
    if (!form) return Promise.reject(new Error(labels.error));
    if (!form.reportValidity()) return Promise.reject(new Error(labels.error));
    return add(new FormData(form), { ...options, form });
  }

  function productForm() {
    return document.querySelector('[data-pdp-form],[data-acc-form]');
  }

  function knownForm(form) {
    return form.matches('[data-pdp-form],[data-acc-form],.pil-cc__form,.pil-coll-caisse__form') || Boolean(form.closest('[data-pil-coll-verres],product-form'));
  }

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !knownForm(form)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    addForm(form, { trigger: event.submitter || form.querySelector('[type="submit"]') }).catch(() => {});
  }, true);

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest?.('[data-pil-add],[data-cart-add],[data-pfav] [data-add],[data-pdp-atc],[data-acc-add],[data-pdp-sticky-buzz],[data-mcta-add],[data-pdp-root] .pil-orbm--add,[data-pdp-buynow]');
    if (!trigger) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (trigger.disabled || trigger.getAttribute('aria-disabled') === 'true') return;
    const id = trigger.getAttribute('data-pil-add') || trigger.getAttribute('data-cart-add') || trigger.getAttribute('data-add');
    if (id) {
      add({ id, quantity: 1 }, { trigger }).catch(() => {});
    } else {
      const form = trigger.closest('form') || productForm();
      addForm(form, { trigger, checkout: trigger.matches('[data-pdp-buynow]') }).catch(() => {});
    }
  }, true);

  document.addEventListener('cart:refresh', () => refresh().catch(() => {}));
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) refreshCount().catch(() => {});
  });
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event?.source === 'pilenium-cart') return;
      if (Number.isFinite(Number(event?.cartData?.item_count))) updateCount(event.cartData);
      else refreshCount().catch(() => {});
    });
  }
  window.PileniumCart = { add, addForm, refresh, refreshCount, updateCount };
})();
